
/**
 * defa.views.UnitSettingsPanel
 * The main unit-panel for changing and setting up any setting with the Unit in focus.
 * @version 1.0.7
 */
import Container                       from 'scripts/poppet/container';
import template                        from 'templates/unitSettingsPanel.jst.html';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelUser   = Bb.Radio.channel( 'user' ),
	channelNav    = Bb.Radio.channel( 'nav' );

const channel = Bb.Radio.channel( 'objects' );

const selectors = {
	regionContainer              : '[data-region=settings-container]',
	buttonTest                   : '[data-click=settings-test]',
	buttonRetest                 : '[data-click=settings-retest]',
	buttonComplaint              : '[data-click=settings-complaint]',
	buttonAlias                  : '[data-click=settings-alias]',
	buttonVehicle                : '[data-click=settings-vehicle]',
	buttonInstall                : '[data-click=settings-install]',
	buttonSubscription           : '[data-click=settings-subscription]',
	buttonDevices                : '[data-click=settings-devices]',
	buttonOpen                   : '[data-click=settings-open]',
	buttonConfigure              : '[data-click=settings-configure]',
	buttonReset                  : '[data-click=settings-reset]',
};

const UnitSettingsPanel = Container.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template : template,
	className: 'card unit-panel',
	
	ui: {
		// owner buttons:
		alias       : selectors.buttonAlias,
		vehicle     : selectors.buttonVehicle,
		subscription: selectors.buttonSubscription,
		
		// installer buttons:
		test     : selectors.buttonTest,
		retest   : selectors.buttonRetest,
		complaint: selectors.buttonComplaint,
		
		// everyone buttons:
		install  : selectors.buttonInstall,
		devices  : selectors.buttonDevices,
		open     : selectors.buttonOpen,
		configure: selectors.buttonConfigure,
		reset    : selectors.buttonReset,
	},
	
	regions: {
		container              : selectors.regionContainer,
	},
	
	events: {
		'click @ui.alias'       : 'onClickAlias',
		'click @ui.vehicle'     : 'onClickVehicle',
		'click @ui.subscription': 'onClickSubscription',
		
		'click @ui.test'     : 'onClickTest',
		'click @ui.retest'   : 'onClickRetest',
		'click @ui.complaint': 'onClickComplaint',
		
		'click @ui.install'  : 'onClickInstall',
		'click @ui.devices'  : 'onClickDevices',
		'click @ui.open'     : 'onClickOpen',
		'click @ui.configure': 'onClickConfigure',
		'click @ui.reset'    : 'onClickReset',
	},
	
	modelEvents: {
		'change:alias': 'onChangeAlias',
	},
	
	templateContext: function () {
		return {
			icon    : this.model.get( 'family' ).toLowerCase(),
			firmware: this.model.get( 'transientFirmware' ),
			isNative: channelLayout.request( 'is:native' ),
			
			token  : false,
			testing: false,
			offline: this.model.get( 'transientOffline' ),
			
			subscriptionName: this.subscriptionName(),
			
			isOwner: this.isOwner(),
			
			isAutomotive: this.isAutomotive(),
			isBlueTooth : this.isBlueTooth(),
			isHome      : this.isHome(),
			
			isInstaller          : this.isInstaller(),
			isInstallerHome      : this.isInstallerHome(),
			isCurrentInstaller   : this.isCurrentInstaller(),
			isInstallerAutomotive: this.isInstallerAutomotive(),
			isInstallerInsurance : this.isInstallerInsurance(),
			isTechSupport        : this.isTechSupport(),
			
			canControlAuto: this.canControlAuto(),
			canControlHome: this.canControlHome(),
		};
	},
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// lifecycle events
	
	/**
	 * Triggers just before the view is shown.
	 * @void
	 */
	onRender: function () {
		Container.prototype.onRender.call( this );
	},
	
	// @ui events
	
	/**
	 * Triggers when a user clicks a "test" button.
	 * @void
	 */
	onClickTest: function () {
		this.trigger( 'nav', 'unit:test', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a user clicks a "retest" button.
	 * @void
	 */
	onClickRetest: function () {
		
		// reset model
		this.model.set( {
			tested           : false,
			transientFirmware: false,
			transientTesting : true,
		} );
		
		this.trigger( 'nav', 'unit:test', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when an installer clicks the "complaint" button.
	 * @void
	 */
	onClickComplaint: function () {
		this.trigger( 'nav', 'unit:complaint', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a user clicks an "alias" button.
	 * @void
	 */
	onClickAlias: function () {
		this.trigger( 'nav', 'unit:alias', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a user clicks a "vehicle" button.
	 * @void
	 */
	onClickVehicle: function () {
		this.trigger( 'nav', 'unit:vehicle', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a user clicks a "subscription" button.
	 * @void
	 */
	onClickSubscription: function () {
		this.trigger( 'nav', 'subscription:panel', [ this.model.get( 'transientSubscription' ), ] );
	},
	
	/**
	 * Triggers when a user clicks a "devices" button.
	 * @void
	 */
	onClickDevices: function () {
		this.trigger( 'nav', 'unit:list' );
	},
	
	/**
	 * Triggers when a user clicks an "open" button.
	 * TODO: Move this to the planned /control/%s/
	 * @void
	 */
	onClickOpen: function () {
		this.trigger( 'open', this );
	},
	
	/**
	 * Triggers when a user clicks a "configure" button.
	 * @void
	 */
	onClickConfigure: function () {
		// swap green button for the install button
		this.ui.configure.removeClass( 'primary' );
		this.ui.install.addClass( 'primary' );
		
		// then behave like an "open" call
		this.onClickOpen();
	},
	
	/**
	 * Triggers when the user clicks the reset button.
	 * @void
	 */
	onClickReset: function () {
		this.trigger( 'nav', 'unit:reset', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a user clicks an "install" button.
	 * @void
	 */
	onClickInstall: function () {
		this.trigger( 'nav', 'unit:install', [ this.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when the alias changes
	 * @void
	 */
	onChangeAlias: function () {
		this.$el.find( 'h4' ).html( this.model.get( 'alias' ) );
	},
	
	onChildviewRetest: function () {
		
		this.model.set( {
			tested           : false,
			transientFirmware: false,
			transientTesting : true,
		} );
		
		this.trigger( 'test', this.model.get( 'id' ) );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Attaches a designated view into the container region.
	 * @param {Object} options  All the options for this Layout, may contain a key->value pair named subView, where the
	 *     value is a string.
	 * @void
	 */
	attachView: function () {
		
		switch ( this.options.subView ) {
			
			case 'install' :
				this._hideFieldsets();
				this._showInstallerView();
				break;
			
			default :
				this._showFieldsets();
				this.getRegion( this.mainRegion ).empty();
				break;
			
		}
	},
	
	/**
	 * Checks if the current set of options matches the requirements for the current view.
	 * @return {bool} True if all requirements pass, false if not.
	 */
	checkRequirements: function ( options ) {
		
		switch ( this.options.view ) {
			
			// the update option requires a tested unit.
			case 'panel' :
				if ( !this.options.unit.get( 'tested' ) ) {
					channelNav.trigger( 'unit:test', [ this.options.unit.get( 'id' ), ] );
					return false;
				}
				
				break;
		}
		return true;
	},
	
	// - private functions
	
	/**
	 * Hides all the fieldsets in this Layout.
	 * @void
	 */
	_hideFieldsets: function () {
		this.$el.find( 'fieldset' ).prop( 'hidden', true );
	},
	
	/**
	 * Hides all the fieldsets in this Layout.
	 * @void
	 */
	_showFieldsets: function () {
		this.$el.find( 'fieldset' ).removeAttr( 'prop' );
	},
	
	
	
	
	
	// - template helper functions
	
	/**
	 * Gets the connected Subscription or SubscriptionPackage's name, if either exists.
	 * @return {String/bool}  The name of the Subscription or the name of the SubscriptionPackage, or false if neither
	 *     exist.
	 */
	subscriptionName: function () {
		
		let subscription = channel.request( 'subscriptions' ).get( this.model.get( 'transientSubscription' ) );
		
		if ( subscription ) {
			return subscription.get( 'transientName' );
		} else {
			return ( this.model.get( 'transientSubscriptionPackage' ) )
				   ? channel.request( 'subscriptionPackages' ).get( this.model.get( 'transientSubscriptionPackage' ) ).get( 'name' )
				   : false;
		}
	},
	
	// - unit types
	
	/**
	 * Checks if the unit is an Automotive device.
	 * @return {bool}  True if the unit is an Automotive device, false if not.
	 */
	isAutomotive: function () {
		return ( 'AUTOMOTIVE' === this.model.get( 'family' ) );
	},
	
	/**
	 * Checks if the unit is a Home device.
	 * @return {bool}  True if the unit is a Home device, false if not.
	 */
	isHome: function () {
		return ( 'HOME' === this.model.get( 'family' ) );
	},
	
	/**
	 * Checks if the unit is a BlueTooth device.
	 * @return {bool}  True if the unit is a BlueTooth device, false if not.
	 */
	isBlueTooth: function () {
		return ( 'BLUETOOTH' === this.model.get( 'family' ) );
	},
	
	// - user status privileges
	
	/**
	 * Checks if the current user is the unit owner.
	 * @return {boolean}  True if the user is the current unit owner, false if not.
	 */
	isOwner: function () {
		
		// technical support owns all units
		if ( this.isTechSupport() ) {
			return true;
		}
		
		let subscription = channel.request( 'subscriptions' ).get( this.model.get( 'transientSubscription' ) );
		
		// a unit without a subscription has no owner
		if ( !subscription ) {
			return false;
		} else {
			
			// get owner id and user customer id's
			let user  = channelUser.request( 'model' ),
				owner = subscription.get( 'owner' ),
				ids   = user.getCustomers().pluck( 'id' );
			
			return _.contains( ids, owner );
			
		}
	},
	
	/**
	 * Checks if the user is any kind of installer.
	 * @return {bool}  True if the user is any kind of installer, false if not.
	 */
	isInstaller: function () {
		return this.options.user.hasRoles( [
			'INSTALLER_AUTOMOTIVE',
			'INSTALLER_HOME',
			'INSTALLER_AUTOMOTIVE_DENMARK_SECURITY',
		] );
	},
	
	/**
	 * Checks if the user can install automotive devices.
	 * @return {bool} True if the user can install automotive devices, false if not.
	 */
	isInstallerAutomotive: function () {
		return this.options.user.hasRoles( [ 'INSTALLER_AUTOMOTIVE', ] );
	},
	
	/**
	 * Checks if the user can install home-devices.
	 * @return {bool} True if the user can install home devices, false if not.
	 */
	isInstallerHome: function () {
		return this.options.user.hasRoles( [ 'INSTALLER_HOME', ] );
	},
	
	/**
	 * Checks if the current user is the installer of the current unit.
	 * @return {bool} True if the installer id matches, false if not.
	 */
	isCurrentInstaller: function () {
		let customerIds = this.options.user.getCustomers().pluck( 'id' );
		return _.contains( customerIds, this.model.get( 'transientInstallerId' ) );
	},
	
	/**
	 * Checks if the user can install insurance-companies
	 * @return {bool} True if the user can install insurance companies, false if not.
	 */
	isInstallerInsurance: function () {
		return this.options.user.hasRoles( [ 'INSTALLER_AUTOMOTIVE_DENMARK_SECURITY', ] );
	},
	
	isTechSupport: function () {
		return channelUser.request( 'model' ).hasRoles( [ 'TECHNICAL_SUPPORT', ] );
	},
	// - user control privileges
	
	/**
	 * Checks if the GUI should allow control of AUTOMOTIVE devices.
	 * @return {bool}  True if the GUI can open an automotive device, false if not.
	 */
	canControlAuto: function () {
		
		// If it's not an automotive, return quick
		if ( 'AUTOMOTIVE' !== this.model.get( 'family' ) ) {
			return false;
		}
		
		// tech support can always open automotive units
		if ( this.isTechSupport() ) {
			return true;
		}
		
		// if there is a subscription, an owner can open.
		if ( this.model.get( 'transientSubscription' ) ) {
			
			// !TODO: make an "ownedBy" function on the unit or something.
			let owner     = channel.request( 'subscriptions' ).get( this.model.get( 'transientSubscription' ) ).get( 'owner' );
			let customers = channelUser.request( 'model' ).getCustomers();
			
			// isOwner check:
			return ( 1 === customers.where( { id: owner } ).length );
		}
		
		// else..
		return true;
	},
	
	/**
	 * Checks if the GUI should allow control of HOME devices.
	 * @return {bool} True if the GUI can open a home device, false if not.
	 */
	canControlHome: function () {
		
		// If it's not a home, return quick.
		if ( 'HOME' !== this.model.get( 'family' ) ) {
			return false;
		}
		
		// tech support can always open home units
		if ( this.isTechSupport() ) {
			return true;
		}
		
		// if there is a subscription, an owner can open.
		if ( this.model.get( 'transientSubscription' ) ) {
			
			// !TODO: make an "ownedBy" function on the unit or something.
			let owner     = channel.request( 'subscriptions' ).get( this.model.get( 'transientSubscription' ) ).get( 'owner' );
			let customers = channelUser.request( 'model' ).getCustomers();
			
			// isOwner check:
			return ( 0 !== customers.where( { id: owner } ).length );
		}
		
		//else..
		return true;
	},
	
	/**
	 * Checks if the installer has clicked the "Configure" button.
	 * @void
	 */
	hasConfiguredHome: function () {
		return this.hasConfigured;
	}
	
} );

export default UnitSettingsPanel;